import React, { useContext, useEffect, useState } from 'react';
import { State } from 'store/state';
import { connect } from 'react-redux';
import ProjectAdBanner, { ProjectAdBannerProps } from './ProjectAdBanner';
import styled from '@emotion/styled';
import {
  breakpointToNarrowContentWidth,
  breakpointToContentWidth,
  breakpointToWideContentWidth,
} from 'ds/components/Layout';
import { BreakpointValue } from 'consts/breakpoints';
import { UserAgentContext } from 'hooks/useImageUrlBuilder';
import { isIOS } from 'utils/uaMatchers';
import withEvents from 'analytics/withEvents';
import { IAd } from 'components/ads/types';
import { searchDocLoadingSelector, searchDocEntriesSelector } from 'store/state/domainData/selectors';
import { desktopContentWidthModeSelector } from 'store/state/selectors/search';
import { ContentWidthMode } from 'utils/entities';
import { createSelector } from 'reselect';


const Root = styled.div<{ isIOSDevice: boolean; contentWidthMode: ContentWidthMode; }>`
  position: fixed;
  bottom: 0;
  z-index: 13;
  will-change: transform;
  transform: translateY(100%);
  animation: .15s linear .2s forwards slideUp;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => `
    background: ${theme.colors.neutrals.white};
    ${theme.isRTL ? 'right' : 'left'}: 0;
    padding-${theme.isRTL ? 'left' : 'right'}: ${theme.spacing(1.5)};
    border-top: 1px solid ${theme.colors.neutrals.grey8};
  `}

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    padding: ${theme.spacing(0.5)} 0;
  `}

  ${({ theme }) => theme.media.showAt({ from: 2 })`
    padding: 0;
  `}

  ${({ theme, isIOSDevice }) => theme.media.showAt({ to: 2 })`
    ${isIOSDevice && 'padding-bottom: 24px;'}
  `}

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  ${({ theme, contentWidthMode }) => Object.keys(breakpointToNarrowContentWidth).map(key => {
    return theme.media.showAt({ at: (+key as BreakpointValue) }) `
      width: ${breakpointToContentWidth[key]};
      ${contentWidthMode === ContentWidthMode.Narrow ? `
        width: ${breakpointToNarrowContentWidth[key]};
      ` : ''}
      ${contentWidthMode === ContentWidthMode.Wide ? `
        width: ${breakpointToWideContentWidth[key]};
      ` : ''}
    `;
  })}
`;

interface StickyProjectAdBannerProps extends ProjectAdBannerProps {
  onBannerExpose?: (banner: IAd) => void;
  isDocLoading: boolean;
  isDocExist: boolean;
  contentWidthMode: ContentWidthMode;
}

const StickyProjectAdBanner: React.FC<StickyProjectAdBannerProps> = props => {
  const [ exposedAdId, setExposedAdId ] = useState<string>(null);
  const userAgent = useContext(UserAgentContext);
  const isIOSDevice = isIOS(userAgent);

  const { ad, isDocLoading, onBannerExpose, contentWidthMode, isDocExist: isDocsExist } = props;

  useEffect(() => {
    if (isDocsExist && !isDocLoading && onBannerExpose && ad && exposedAdId !== ad.id) {
      onBannerExpose(ad);
      setExposedAdId(ad.id);
    }
  }, [ ad, onBannerExpose, isDocLoading, isDocsExist ]);

  return ad ? (
    <Root isIOSDevice={isIOSDevice} data-auto="search-sticky-banner-container" contentWidthMode={contentWidthMode}>
      <ProjectAdBanner {...props} />
    </Root>
  ) : null;
};

const StickyProjectAdBannerWithEvents = withEvents<StickyProjectAdBannerProps>(sendEvent => ({
  onBannerExpose({ id, isExternalLink, link }: IAd) {
    sendEvent('banner_exposed', 'banner', {
      event: {
        banner_id: id,
        banner_is_external: isExternalLink,
        banner_url: link,
        banner_index: 1,
        is_closeable: true,
      },
    });
  },
}))(StickyProjectAdBanner);


const isDocExistSelector = createSelector(searchDocEntriesSelector, (docs) => {
  return Boolean(docs && docs.length);
});

const mapStateToProps = (state: State) => ({
  isDocExist: isDocExistSelector(state),
  isDocLoading: searchDocLoadingSelector(state),
  contentWidthMode: desktopContentWidthModeSelector(state),
});

export default connect(mapStateToProps)(StickyProjectAdBannerWithEvents);
